"use client";

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/form-field";
import { StatusBadge } from "@/components/status-badge";
import type { UtmParams } from "@/lib/utm";

export default function SignupForm({
  utm,
  defaultEmail = "",
}: {
  utm?: UtmParams;
  defaultEmail?: string;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const requestLink = async () => {
    setSending(true);
    setError("");
    try {
      const res = await fetch("/api/auth/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, utm }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Something went wrong. Try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Couldn't reach the server. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }
    requestLink();
  };

  return (
    <>
      {!submitted ? (
        <form onSubmit={handleSubmit} className="grid gap-4">
          <FormField
            label="Email"
            id="su-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          {error && <p className="text-destructive text-[13px] m-0">{error}</p>}
          <Button type="submit" className="w-full" disabled={sending}>
            {sending ? "Sending…" : "Send sign in link →"}
          </Button>
          <p className="text-xs opacity-60 text-center m-0">
            No password needed — we&apos;ll email you a link.
          </p>
        </form>
      ) : (
        <div>
          <StatusBadge variant="positive" className="mb-4">
            Link sent
          </StatusBadge>
          <p>
            Check <strong>{email}</strong> for a sign in link. Click it to continue — new accounts
            land in the control panel, existing ones sign right in.
          </p>
          <Button
            type="button"
            variant="outline"
            className="w-full mt-4"
            disabled={sending}
            onClick={requestLink}
          >
            {sending ? "Sending…" : "Resend link"}
          </Button>
          {/* requestLink() is shared with the initial submit, whose error
              display lives in the form above — this view needs its own so a
              failed *resend* isn't silently swallowed. */}
          {error && <p className="text-destructive text-[13px] mt-2 mb-0">{error}</p>}
        </div>
      )}
    </>
  );
}
